import { DG } from '@cotera/sdk/core';
import { assertExists } from '@cotera/utilities';
import { ok, err } from 'neverthrow';
import { QueryResult, StorageConnection } from './storage-connection';

const BRANCH_IDS_LOCATION = (orgID: string) =>
  `${orgID}/userbranches/branchIDs`;
const CURRENT_BRANCH_ID_LOCATION = (orgID: string) =>
  `${orgID}/userbranches/current-branch-id`;
const BRANCH_OPERATIONS_LOCATION = (orgID: string, branchID: string) =>
  `${orgID}/userbranches/operations/${branchID}`;
const AUTO_PUBLISH_LOCATION = (orgID: string) => `${orgID}/auto-publish`;
/**
 * Implementation for the (low-level) datastructures specified by StorageSolution.
 *
 * This implementation uses localStorage under the hood.
 *
 * Note: Instantiation of this class does not reset storage, but rather
 * interfaces as a continuation to the previous session's storage.
 */
export class LocalStorageConnection implements StorageConnection {
  readonly branchIDsLocation: string;
  readonly currentBranchIDLocation: string;
  readonly getBranchOperationsLocation: (branchID: string) => string;
  private readonly listeners: Record<string, ((value: any) => void)[]> = {};

  constructor(private readonly orgId: string) {
    this.branchIDsLocation = BRANCH_IDS_LOCATION(orgId); // rename to branchid
    this.currentBranchIDLocation = CURRENT_BRANCH_ID_LOCATION(orgId);
    this.getBranchOperationsLocation = (branchID: string) =>
      BRANCH_OPERATIONS_LOCATION(orgId, branchID);

    if (localStorage.getItem(this.branchIDsLocation) == null) {
      localStorage.setItem(this.branchIDsLocation, JSON.stringify([]));
    }
  }

  async getAutoPublish(): Promise<boolean> {
    //default to true
    const storageItem =
      localStorage.getItem(AUTO_PUBLISH_LOCATION(this.orgId)) ?? 'true';
    return storageItem === 'true';
  }

  async setAutoPublish(autoPublish: boolean): Promise<QueryResult<'ok'>> {
    localStorage.setItem(
      AUTO_PUBLISH_LOCATION(this.orgId),
      autoPublish.toString()
    );
    return ok('ok');
  }

  async getBranchIDs(): Promise<string[]> {
    // Guaranteed to exist because initially set in constructor
    const keys = assertExists(localStorage.getItem(this.branchIDsLocation));
    return JSON.parse(keys) as string[]; // Gauranteed to parse
  }

  async addBranchID(branchID: string): Promise<QueryResult<'ok'>> {
    if (await this.hasBranchID(branchID)) {
      return err({ t: 'duplicate-branchID', branchID });
    }

    const currentKeys = await this.getBranchIDs();
    const newKeys = [...currentKeys, branchID];

    localStorage.setItem(this.branchIDsLocation, JSON.stringify(newKeys));
    localStorage.setItem(
      this.getBranchOperationsLocation(branchID),
      JSON.stringify([])
    );

    return ok('ok');
  }

  async hasBranchID(branchID: string): Promise<boolean> {
    const keys = await this.getBranchIDs();
    return keys.includes(branchID);
  }

  async getCurrentBranchID(): Promise<string | null> {
    // If key does not exist, returs null
    return localStorage.getItem(this.currentBranchIDLocation);
  }

  async setCurrentBranchID(branchID: string): Promise<QueryResult<'ok'>> {
    if (!(await this.hasBranchID(branchID))) {
      return err({ t: 'non-existing-branchID', branchID });
    }
    localStorage.setItem(this.currentBranchIDLocation, branchID);

    return ok('ok');
  }

  async getOperations(
    branchID: string
  ): Promise<QueryResult<DG.OperationData[]>> {
    if (!(await this.hasBranchID(branchID))) {
      return err({ t: 'non-existing-branchID', branchID });
    }

    const operationsSerialized = localStorage.getItem(
      this.getBranchOperationsLocation(branchID)
    );

    const operations =
      operationsSerialized === null
        ? []
        : (JSON.parse(operationsSerialized) as DG.OperationData[]); // guaranteed to parse

    return ok(operations);
  }

  async addOperations(
    branchID: string,
    operations: DG.OperationData[]
  ): Promise<QueryResult<'ok'>> {
    const currentOperations = await this.getOperations(branchID);

    if (currentOperations.isErr()) {
      return err(currentOperations.error); // propagate non-existing-branchID error
    }

    const newOperations = [...currentOperations.value, ...operations];

    localStorage.setItem(
      this.getBranchOperationsLocation(branchID),
      JSON.stringify(newOperations)
    );

    return ok('ok');
  }

  async clearOperations(branchID: string): Promise<QueryResult<'ok'>> {
    if (!(await this.hasBranchID(branchID))) {
      return err({ t: 'non-existing-branchID', branchID });
    }

    localStorage.setItem(
      this.getBranchOperationsLocation(branchID),
      JSON.stringify([])
    );

    return ok('ok');
  }

  getItem<T>(key: string, defaultValue: T): T {
    const itemKey = this.makeItemKey(key);
    const item = localStorage.getItem(itemKey);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  }

  setItem<T>(key: string, value: T): Promise<void> {
    const itemKey = this.makeItemKey(key);
    localStorage.setItem(itemKey, JSON.stringify(value));
    this.listeners[itemKey]?.forEach((l) => l(value));
    return Promise.resolve();
  }

  subscribe<T>(key: string, listener: (value: T) => void): () => void {
    const itemKey = this.makeItemKey(key);
    if (!this.listeners[itemKey]) {
      this.listeners[itemKey] = [];
    }
    this.listeners[itemKey].push(listener);
    return () => {
      this.listeners[itemKey] = this.listeners[itemKey]!.filter(
        (l) => l !== listener
      );
    };
  }

  private makeItemKey(key: string): string {
    return `${this.orgId}/${key}`;
  }
}

export class InMemoryStorageConnection implements StorageConnection {
  private readonly branchIDs: string[] = [];
  private currentBranchID: string | null = null;
  private readonly operations: Record<string, string> = {};
  private autoPublish: boolean = true;
  private readonly items: Record<string, any> = {};
  private readonly listeners: Record<string, ((value: any) => void)[]> = {};

  async getAutoPublish(): Promise<boolean> {
    return this.autoPublish;
  }

  async setAutoPublish(autoPublish: boolean): Promise<QueryResult<'ok'>> {
    this.autoPublish = autoPublish;
    return ok('ok');
  }

  async clearOperations(branchID: string): Promise<QueryResult<'ok'>> {
    this.operations[branchID] = '';
    return ok('ok');
  }

  async getBranchIDs(): Promise<string[]> {
    return this.branchIDs;
  }

  async addBranchID(branchID: string): Promise<QueryResult<'ok'>> {
    this.branchIDs.push(branchID);
    return ok('ok');
  }

  async hasBranchID(branchID: string): Promise<boolean> {
    return this.branchIDs.includes(branchID);
  }

  async getCurrentBranchID(): Promise<string | null> {
    return this.currentBranchID;
  }

  async setCurrentBranchID(branchID: string): Promise<QueryResult<'ok'>> {
    this.currentBranchID = branchID;
    return ok('ok');
  }

  async getOperations(
    branchID: string
  ): Promise<QueryResult<DG.OperationData[]>> {
    if (!(await this.hasBranchID(branchID))) {
      return err({ t: 'non-existing-branchID', branchID });
    }

    const ops = this.operations[branchID];
    if (!ops) {
      return ok([]);
    }

    try {
      return ok(JSON.parse(ops));
    } catch {
      return ok([]);
    }
  }

  async addOperations(
    branchID: string,
    operations: DG.OperationData[]
  ): Promise<QueryResult<'ok'>> {
    if (!(await this.hasBranchID(branchID))) {
      return err({ t: 'non-existing-branchID', branchID });
    }

    const existingOps = await this.getOperations(branchID);
    if (existingOps.isErr()) {
      return err(existingOps.error);
    }

    const newOps = [...existingOps.value, ...operations];
    this.operations[branchID] = JSON.stringify(newOps);
    return ok('ok');
  }

  getItem<T>(key: string, defaultValue: T): T {
    return (this.items[key] as T) ?? defaultValue;
  }

  setItem<T>(key: string, value: T): Promise<void> {
    this.items[key] = value;
    this.listeners[key]?.forEach((l) => l(value));
    return Promise.resolve();
  }

  subscribe<T>(key: string, listener: (value: T) => void): () => void {
    if (!this.listeners[key]) {
      this.listeners[key] = [];
    }
    this.listeners[key].push(listener);
    return () => {
      this.listeners[key] = this.listeners[key]!.filter((l) => l !== listener);
    };
  }
}
